import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  /**
   * Optional JSON-LD structured data. Pass an object or array of objects.
   * Rendered as a single <script type="application/ld+json"> tag managed by this hook.
   */
  jsonLd?: object | object[];
}

const DEFAULT_OG_IMAGE =
  'https://storage.googleapis.com/gpt-engineer-file-uploads/P3WgZdiTwwUX52k2AdbypNSfHFj1/social-images/social-1777851984018-Screenshot_2026-05-04_094612.webp';

const JSONLD_ID = 'seo-jsonld';

export const useSEO = ({ title, description, canonical, ogTitle, ogDescription, ogImage, jsonLd }: SEOProps) => {
  useEffect(() => {
    // Title
    document.title = title;

    // Helper to set/create meta tags
    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', description);

    // OG tags
    const resolvedOgTitle = ogTitle || title;
    const resolvedOgDesc = ogDescription || description;
    const resolvedOgImage = ogImage || DEFAULT_OG_IMAGE;

    setMeta('meta[property="og:title"]', 'content', resolvedOgTitle);
    setMeta('meta[property="og:description"]', 'content', resolvedOgDesc);
    setMeta('meta[property="og:image"]', 'content', resolvedOgImage);

    setMeta('meta[name="twitter:title"]', 'content', resolvedOgTitle);
    setMeta('meta[name="twitter:description"]', 'content', resolvedOgDesc);
    setMeta('meta[name="twitter:image"]', 'content', resolvedOgImage);

    // Canonical — always self-canonical (https, non-www, no trailing slash except homepage)
    if (canonical) {
      // Defensive: strip trailing slash unless it's the root "/"
      let normalized = canonical;
      try {
        const u = new URL(canonical);
        if (u.pathname !== '/' && u.pathname.endsWith('/')) {
          u.pathname = u.pathname.replace(/\/+$/, '');
        }
        normalized = u.toString();
      } catch {
        // Fallback for non-absolute values
        if (canonical !== '/' && canonical.endsWith('/')) {
          normalized = canonical.replace(/\/+$/, '');
        }
      }

      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = normalized;
      // Keep og:url in sync with the canonical URL
      setMeta('meta[property="og:url"]', 'content', normalized);
    }

    // JSON-LD
    const existing = document.getElementById(JSONLD_ID);
    if (jsonLd) {
      const script = (existing as HTMLScriptElement | null) ?? document.createElement('script');
      script.id = JSONLD_ID;
      script.setAttribute('type', 'application/ld+json');
      script.textContent = JSON.stringify(jsonLd);
      if (!existing) document.head.appendChild(script);
    } else if (existing) {
      existing.remove();
    }
  }, [title, description, canonical, ogTitle, ogDescription, ogImage, jsonLd]);
};
